'use strict';
/**
 * Workbook-Writer.js — a minimal, zero-dependency OOXML (.xlsx) writer.
 *
 * WHY THIS EXISTS RATHER THAN A LIBRARY. The dashboards need a flat grid with a
 * header row, frozen panes and real numeric cells -- the thinnest slice of the
 * spreadsheet spec. Measured against this bundle with its own toolchain, SheetJS
 * costs +865 KB and ExcelJS +943 KB (roughly +20% of a 4.3 MB bundle), while this
 * writer costs about +5.7 KB. A library buys formulas, charts, images, merged
 * cells and reading .xlsx back in; none of that is wanted here. If any of it ever
 * is, the trade flips and SheetJS is the answer.
 *
 * What it does NOT do, stated plainly so nobody discovers it the hard way:
 * no formulas, no merged cells, no autofilter, no date cells (dates go out as
 * strings), no cell-level number formats. Column widths and a bold header are in.
 *
 * Browser-safe by construction: Uint8Array only, no node builtins, nothing that
 * browserify has to shim.
 *
 * @author HeadLight
 */

/** Precomputed CRC-32 table; the zip central directory needs a CRC per entry. */
const _CRCTable = (() =>
{
	const tmpTable = new Int32Array(256);
	for (let i = 0; i < 256; i++)
	{
		let tmpValue = i;
		for (let j = 0; j < 8; j++)
		{
			tmpValue = (tmpValue & 1) ? (0xEDB88320 ^ (tmpValue >>> 1)) : (tmpValue >>> 1);
		}
		tmpTable[i] = tmpValue;
	}
	return tmpTable;
})();

/**
 * @param {Uint8Array} pBuffer
 * @return {number} The CRC-32 of the buffer.
 */
function crc32(pBuffer)
{
	let tmpCRC = -1;
	for (let i = 0; i < pBuffer.length; i++)
	{
		tmpCRC = _CRCTable[(tmpCRC ^ pBuffer[i]) & 0xFF] ^ (tmpCRC >>> 8);
	}
	return (tmpCRC ^ -1) >>> 0;
}

/**
 * UTF-8 encode. Written out rather than relying on TextEncoder because this app's
 * browserslist still resolves old browsers, and a missing TextEncoder would fail
 * at the moment of export rather than at load.
 *
 * @param {string} pText
 * @return {Uint8Array}
 */
function encodeUTF8(pText)
{
	const tmpText = String(pText == null ? '' : pText);
	if (typeof TextEncoder !== 'undefined') { return new TextEncoder().encode(tmpText); }
	const tmpBytes = [];
	for (let i = 0; i < tmpText.length; i++)
	{
		let tmpCode = tmpText.charCodeAt(i);
		if (tmpCode < 0x80) { tmpBytes.push(tmpCode); }
		else if (tmpCode < 0x800) { tmpBytes.push(0xC0 | (tmpCode >> 6), 0x80 | (tmpCode & 0x3F)); }
		else if ((tmpCode >= 0xD800) && (tmpCode <= 0xDBFF))
		{
			// Surrogate pair -> one code point.
			const tmpLow = tmpText.charCodeAt(++i);
			tmpCode = 0x10000 + ((tmpCode - 0xD800) << 10) + (tmpLow - 0xDC00);
			tmpBytes.push(0xF0 | (tmpCode >> 18), 0x80 | ((tmpCode >> 12) & 0x3F), 0x80 | ((tmpCode >> 6) & 0x3F), 0x80 | (tmpCode & 0x3F));
		}
		else { tmpBytes.push(0xE0 | (tmpCode >> 12), 0x80 | ((tmpCode >> 6) & 0x3F), 0x80 | (tmpCode & 0x3F)); }
	}
	return new Uint8Array(tmpBytes);
}

/**
 * Pack files into a STORE (uncompressed) zip.
 *
 * STORE rather than DEFLATE keeps this dependency-free. The cost is file size on
 * very large exports; XML compresses well, so a deflate path is the upgrade if
 * that ever bites -- see the note in the module header.
 *
 * @param {Array<{name:string, data:Uint8Array}>} pFiles
 * @return {Uint8Array} The zip bytes.
 */
function storeZip(pFiles)
{
	const tmpParts = [];
	const tmpCentral = [];
	let tmpOffset = 0;
	const u16 = (pValue) => [ pValue & 0xFF, (pValue >>> 8) & 0xFF ];
	const u32 = (pValue) => [ pValue & 0xFF, (pValue >>> 8) & 0xFF, (pValue >>> 16) & 0xFF, (pValue >>> 24) & 0xFF ];
	for (const tmpFile of pFiles)
	{
		const tmpNameBytes = encodeUTF8(tmpFile.name);
		const tmpCRC = crc32(tmpFile.data);
		const tmpSize = tmpFile.data.length;
		// 0x0800 = the UTF-8 filename flag.
		const tmpLocal = new Uint8Array([].concat(
			u32(0x04034b50), u16(20), u16(0x0800), u16(0), u16(0), u16(0),
			u32(tmpCRC), u32(tmpSize), u32(tmpSize), u16(tmpNameBytes.length), u16(0)));
		tmpParts.push(tmpLocal, tmpNameBytes, tmpFile.data);
		tmpCentral.push(new Uint8Array([].concat(
			u32(0x02014b50), u16(20), u16(20), u16(0x0800), u16(0), u16(0), u16(0),
			u32(tmpCRC), u32(tmpSize), u32(tmpSize), u16(tmpNameBytes.length),
			u16(0), u16(0), u16(0), u16(0), u32(0), u32(tmpOffset))), tmpNameBytes);
		tmpOffset += tmpLocal.length + tmpNameBytes.length + tmpSize;
	}
	const tmpDirectoryStart = tmpOffset;
	let tmpDirectorySize = 0;
	for (const tmpEntry of tmpCentral) { tmpDirectorySize += tmpEntry.length; }
	const tmpEnd = new Uint8Array([].concat(
		u32(0x06054b50), u16(0), u16(0), u16(pFiles.length), u16(pFiles.length),
		u32(tmpDirectorySize), u32(tmpDirectoryStart), u16(0)));
	let tmpTotal = 0;
	for (const tmpPart of tmpParts) { tmpTotal += tmpPart.length; }
	tmpTotal += tmpDirectorySize + tmpEnd.length;
	const tmpOut = new Uint8Array(tmpTotal);
	let tmpCursor = 0;
	for (const tmpPart of tmpParts) { tmpOut.set(tmpPart, tmpCursor); tmpCursor += tmpPart.length; }
	for (const tmpEntry of tmpCentral) { tmpOut.set(tmpEntry, tmpCursor); tmpCursor += tmpEntry.length; }
	tmpOut.set(tmpEnd, tmpCursor);
	return tmpOut;
}

/** @param {*} pValue @return {string} XML-escaped text. */
const escapeXML = (pValue) => String(pValue == null ? '' : pValue)
	.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** @param {number} pIndex @return {string} Zero-based column index as A, B, ... AA. */
function columnName(pIndex)
{
	let tmpName = '';
	let tmpIndex = pIndex + 1;
	while (tmpIndex > 0)
	{
		const tmpRemainder = (tmpIndex - 1) % 26;
		tmpName = String.fromCharCode(65 + tmpRemainder) + tmpName;
		tmpIndex = (tmpIndex - tmpRemainder - 1) / 26;
	}
	return tmpName;
}

/**
 * A workbook sheet's XML.
 *
 * A cell is numeric ONLY when the value is already a JS number -- the caller
 * decides what becomes a number and what stays text. That split is deliberate:
 * these dashboards store measurements as STRINGS on purpose, to keep arbitrary
 * precision away from JavaScript's float mantissa, so the conversion belongs at
 * the one boundary where a number is actually required and must be done against
 * the column's declared precision.
 *
 * @param {{name:string, columns:Array<{Label:string}>, rows:Array<Array<*>>}} pSheet
 * @return {string}
 */
function buildSheetXML(pSheet)
{
	const tmpRows = [];
	tmpRows.push('<row r="1">' + pSheet.columns.map((pColumn, pIndex) =>
		`<c r="${columnName(pIndex)}1" s="1" t="inlineStr"><is><t xml:space="preserve">${escapeXML(pColumn.Label)}</t></is></c>`).join('') + '</row>');
	pSheet.rows.forEach((pRow, pRowIndex) =>
	{
		tmpRows.push(`<row r="${pRowIndex + 2}">` + pRow.map((pValue, pIndex) =>
		{
			const tmpRef = `${columnName(pIndex)}${pRowIndex + 2}`;
			if ((typeof pValue === 'number') && isFinite(pValue)) { return `<c r="${tmpRef}"><v>${pValue}</v></c>`; }
			return `<c r="${tmpRef}" t="inlineStr"><is><t xml:space="preserve">${escapeXML(pValue)}</t></is></c>`;
		}).join('') + '</row>');
	});

	// Column widths from the header text and a sample of the body, so a reviewer
	// does not open the file to a grid of ##### and have to size every column.
	const tmpWidths = pSheet.columns.map((pColumn, pIndex) =>
	{
		let tmpWidest = String(pColumn.Label || '').length;
		const tmpSampleCount = Math.min(pSheet.rows.length, 200);
		for (let i = 0; i < tmpSampleCount; i++)
		{
			const tmpCell = pSheet.rows[i][pIndex];
			const tmpLength = String(tmpCell == null ? '' : tmpCell).length;
			if (tmpLength > tmpWidest) { tmpWidest = tmpLength; }
		}
		return Math.max(8, Math.min(46, tmpWidest + 2));
	});
	const tmpCols = '<cols>' + tmpWidths.map((pWidth, pIndex) =>
		`<col min="${pIndex + 1}" max="${pIndex + 1}" width="${pWidth}" customWidth="1"/>`).join('') + '</cols>';

	return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
		'<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
		'<sheetViews><sheetView workbookViewId="0">' +
		'<pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/>' +
		'</sheetView></sheetViews>' +
		tmpCols +
		`<sheetData>${tmpRows.join('')}</sheetData></worksheet>`;
}

/**
 * Excel rejects a workbook whose sheet names collide, contain []:*?/\\ or exceed
 * 31 characters -- and it rejects it SILENTLY, as "we found a problem with some
 * content", which is a miserable thing to debug. Sanitise here, once.
 *
 * @param {Array<{name:string}>} pSheets
 * @return {Array<string>} Safe, unique sheet names in the same order.
 */
function resolveSheetNames(pSheets)
{
	const tmpUsed = {};
	return pSheets.map((pSheet, pIndex) =>
	{
		let tmpName = String(pSheet.name || `Sheet${pIndex + 1}`).replace(/[\[\]:*?\/\\]/g, ' ').trim().slice(0, 31);
		if (!tmpName) { tmpName = `Sheet${pIndex + 1}`; }
		let tmpCandidate = tmpName;
		let tmpSuffix = 2;
		while (tmpUsed[tmpCandidate.toLowerCase()])
		{
			const tmpTrimmed = tmpName.slice(0, 31 - String(tmpSuffix).length - 1);
			tmpCandidate = `${tmpTrimmed} ${tmpSuffix}`;
			tmpSuffix++;
		}
		tmpUsed[tmpCandidate.toLowerCase()] = true;
		return tmpCandidate;
	});
}

/**
 * Build a complete .xlsx.
 *
 * @param {Array<{name:string, columns:Array<{Label:string}>, rows:Array<Array<*>>}>} pSheets
 * @return {Uint8Array} The .xlsx bytes, ready for a Blob.
 */
function buildXLSX(pSheets)
{
	const tmpSheets = (Array.isArray(pSheets) && pSheets.length > 0) ? pSheets : [ { name: 'Sheet1', columns: [], rows: [] } ];
	const tmpNames = resolveSheetNames(tmpSheets);
	const tmpFiles = [];

	tmpFiles.push({ name: '[Content_Types].xml', data: encodeUTF8(
		'<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
		'<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
		'<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
		'<Default Extension="xml" ContentType="application/xml"/>' +
		'<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
		tmpSheets.map((pSheet, pIndex) =>
			`<Override PartName="/xl/worksheets/sheet${pIndex + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join('') +
		'<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>' +
		'</Types>') });

	tmpFiles.push({ name: '_rels/.rels', data: encodeUTF8(
		'<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
		'<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
		'<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>' +
		'</Relationships>') });

	tmpFiles.push({ name: 'xl/workbook.xml', data: encodeUTF8(
		'<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
		'<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" ' +
		'xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets>' +
		tmpNames.map((pName, pIndex) => `<sheet name="${escapeXML(pName)}" sheetId="${pIndex + 1}" r:id="rId${pIndex + 1}"/>`).join('') +
		'</sheets></workbook>') });

	tmpFiles.push({ name: 'xl/_rels/workbook.xml.rels', data: encodeUTF8(
		'<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
		'<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
		tmpSheets.map((pSheet, pIndex) =>
			`<Relationship Id="rId${pIndex + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${pIndex + 1}.xml"/>`).join('') +
		`<Relationship Id="rId${tmpSheets.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>` +
		'</Relationships>') });

	tmpFiles.push({ name: 'xl/styles.xml', data: encodeUTF8(
		'<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
		'<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
		'<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font>' +
		'<font><b/><color rgb="FFFFFFFF"/><sz val="11"/><name val="Calibri"/></font></fonts>' +
		'<fills count="3"><fill><patternFill patternType="none"/></fill>' +
		'<fill><patternFill patternType="gray125"/></fill>' +
		'<fill><patternFill patternType="solid"><fgColor rgb="FF3273DC"/><bgColor indexed="64"/></patternFill></fill></fills>' +
		'<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>' +
		'<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
		'<cellXfs count="2"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>' +
		'<xf numFmtId="0" fontId="1" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1"/></cellXfs>' +
		'</styleSheet>') });

	tmpSheets.forEach((pSheet, pIndex) =>
	{
		tmpFiles.push({ name: `xl/worksheets/sheet${pIndex + 1}.xml`, data: encodeUTF8(buildSheetXML(pSheet)) });
	});

	return storeZip(tmpFiles);
}

module.exports = { buildXLSX, storeZip, encodeUTF8, escapeXML, columnName, resolveSheetNames };
